// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Select, MenuItem, Dialog, DialogActions, DialogContent, Typography, makeStyles } from '@material-ui/core';
import { connect } from 'react-redux';
import { ArrowDownwardRounded, ArrowUpwardRounded } from '@material-ui/icons';
import { toast } from 'react-toastify';

// Internal Dependencies
import PillButton from '../../common/Button/PillButton';
import OverLoading from '../../global/OverLoading';
import Constants from '../../../common/constants';
import { IsEmpty } from '../../../common/checks';
import { Sleep, swapArray } from '../../../common/util';
import { addMallMap, updateMallMap, deleteMallMap } from '../../../actions/admin/mapAction';
import Colours from '../../../styles/colours';

const formFields = {
  LEVEL: {
    width: '60px',
  },
  MODEL: {
    width: '250px',
  },
  POSITION: {
    width: '100px',
  },
};

const useStyles = makeStyles(() => ({
  input: {
    border:      `1px solid ${Colours.Black}`,
    height:      '30px',
    fontSize:    '14px',
    paddingLeft: '4px',
  },
  levelInput: {
    width: `calc(${formFields.LEVEL.width} - 20px)`,
  },
  modelInput: {
    width: `calc(${formFields.MODEL.width} - 20px)`,
  },
  positionInput: {
    width: `calc(${formFields.POSITION.width} - 20px)`,
  },
  iconContainer: {
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    border:          'none',
    backgroundColor: Colours.Gray4,
    width:           '28px',
    height:          '28px',
    borderRadius:    '50px',
  },
}));

// eslint-disable-next-line no-shadow
const ManageLevelsModal = ({ pageId, isOpen, mallMapData, onClose, addMallMap, updateMallMap, deleteMallMap }) => {
  const classes = useStyles();

  const [manageLevelsData, setManageLevelsData] = useState([]);
  const [removeManageLevels, setRemoveManageLevels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const addLevelHandler = () => {
    const manageLevel = {
      uid:       manageLevelsData.length,
      mallMapId: null,
      level:     null,
      model:     null,
      position:  manageLevelsData.length,
    };

    setManageLevelsData((prevManageLevel) => [...prevManageLevel, manageLevel]);
  };

  const levelHandler = (value, uid) => {
    const copyManageLevelsData = _.cloneDeep(manageLevelsData);
    const manageLevelIndex = _.findIndex(copyManageLevelsData, (manageLevel) => manageLevel.uid === uid);

    copyManageLevelsData[manageLevelIndex].level = value;
    setManageLevelsData(copyManageLevelsData);
  };

  const modelHandler = (value, uid) => {
    const copyManageLevelsData = _.cloneDeep(manageLevelsData);
    const manageLevelIndex = _.findIndex(copyManageLevelsData, (manageLevel) => manageLevel.uid === uid);

    copyManageLevelsData[manageLevelIndex].model = value;
    setManageLevelsData(copyManageLevelsData);
  };

  const deleteManageLevelDataHandler = async (uid) => {
    let copyManageLevelsData = _.cloneDeep(manageLevelsData);
    const removedManageLevel = _.find(copyManageLevelsData, (manageLevel) => manageLevel.uid === uid);
    copyManageLevelsData = _.filter(copyManageLevelsData, (manageLevel) => manageLevel.uid !== uid);

    setManageLevelsData(copyManageLevelsData);
    setRemoveManageLevels((prev) => _.flatMap([...prev, removedManageLevel]));
  };

  const moveManageLevelPositionHandler = (direction, uid) => {
    const copyManageLevelsData = _.cloneDeep(manageLevelsData);
    const currentManageLevelIndex = _.findIndex(copyManageLevelsData, (manageLevel) => manageLevel.uid === uid);
    let indexToSwap = -1;

    if (direction === 'UP') {
      indexToSwap = currentManageLevelIndex - 1; // top index
    } else if (direction === 'DOWN') {
      indexToSwap = currentManageLevelIndex + 1; // bottom index
    }

    swapArray(copyManageLevelsData, currentManageLevelIndex, indexToSwap);
    setManageLevelsData(copyManageLevelsData);
  };

  const formatManageLevelsData = (mallMaps) => {
    const sortMallMaps = _.sortBy(mallMaps, 'position');
    const formattedManageLevel = _.map((sortMallMaps), (mallMap, index) => ({
      uid:       index,
      pageId,
      mallMapId: mallMap.id,
      level:     mallMap.level,
      model:     mallMap.model,
      position:  mallMap.position,
    }));

    return formattedManageLevel;
  };

  const formatManageLevelPayloadData = (manageLevels) => {
    const formattedPayload = _.map(manageLevels, (manageLevel, index) => ({
      pageId,
      mallMapId: manageLevel.mallMapId,
      level:     manageLevel.level,
      model:     manageLevel.model,
      position:  index,
    }));

    return formattedPayload;
  };

  const saveManageLevelDataHandler = async () => {
    try {
      setIsLoading(true);

      const payload = formatManageLevelPayloadData(manageLevelsData);
      const removePayload = formatManageLevelPayloadData(removeManageLevels);

      payload.forEach(async (data) => {
        if (IsEmpty(data.mallMapId)) {
          await addMallMap(data);
        } else {
          await updateMallMap(data.mallMapId, data);
        }
        await Sleep(200);
      });

      if (!IsEmpty(removePayload)) {
        removePayload.forEach(async (data) => {
          await deleteMallMap(data.mallMapId);
          await Sleep(200);
        });

        setRemoveManageLevels([]);
      }

      onClose(true);
    } catch (err) {
      toast.error('Unable to save map data');
      console.log('saveManageLevelDataHandler() Error unable to save map data: ', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const formattedManageLevels = formatManageLevelsData(mallMapData);
      setManageLevelsData(formattedManageLevels);
    }
  }, [isOpen]);

  return (
    <div>
      { (isOpen && isLoading) && <OverLoading /> }
      <Dialog
        fullWidth
        maxWidth="sm"
        open={isOpen}
        aria-labelledby="manage-levels"
        aria-describedby="to manage multiple mall map levels"
        onClose={() => onClose()}
      >
        <DialogContent>
          <Typography variant="h5">Manage Levels</Typography>
          <PillButton text="Add Level" className="mt-2" onClick={addLevelHandler} />
          <hr className="border-dark" />

          <div>
            { manageLevelsData.length ? (
              <div className="d-flex">
                <Typography style={{ width: formFields.LEVEL.width }} variant="h6" className="mb-2">Level</Typography>
                <Typography style={{ width: formFields.MODEL.width }} variant="h6" className="mb-2">Model</Typography>
                <Typography style={{ width: formFields.POSITION.width }} variant="h6" className="mb-2">Position</Typography>
              </div>
            ) : null}

            <div>
              {manageLevelsData.map((manageLevel, index) => (
                <div className="d-flex mb-2" key={manageLevel.uid}>
                  <div style={{ width: formFields.LEVEL.width }}>
                    <input
                      className={clsx([classes.input, classes.levelInput])}
                      value={manageLevel.level || ''}
                      onChange={({ target }) => levelHandler(target.value, manageLevel.uid)}
                    />
                  </div>
                  <div style={{ width: formFields.MODEL.width }}>
                    <Select
                      className={classes.modelInput}
                      value={manageLevel.model || ''}
                      onChange={({ target }) => modelHandler(target.value, manageLevel.uid)}
                    >
                      { Constants.MallNameMapping.map((mallMap) => (
                        <MenuItem key={mallMap} value={mallMap}>{mallMap}</MenuItem>
                      )) }
                    </Select>
                  </div>
                  <div className="d-flex" style={{ width: formFields.POSITION.width, gap: '6px' }}>
                    <button
                      disabled={index === 0}
                      type="button"
                      className={classes.iconContainer}
                      onClick={() => moveManageLevelPositionHandler('UP', manageLevel.uid)}
                    >
                      <ArrowUpwardRounded />
                    </button>
                    <button
                      disabled={index === manageLevelsData.length - 1}
                      type="button"
                      className={classes.iconContainer}
                      onClick={() => moveManageLevelPositionHandler('DOWN', manageLevel.uid)}
                    >
                      <ArrowDownwardRounded />
                    </button>
                  </div>
                  <PillButton text="Delete" onClick={() => deleteManageLevelDataHandler(manageLevel.uid)} />
                </div>
              ))}
            </div>
          </div>
        </DialogContent>

        <DialogActions>
          <PillButton
            text="Save"
            btnStyle="primary"
            className="mx-3 mb-3"
            onClick={saveManageLevelDataHandler}
            disabled={isLoading}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

ManageLevelsModal.propTypes = {
  pageId:        PropTypes.number.isRequired,
  isOpen:        PropTypes.bool,
  mallMapData:   PropTypes.arrayOf(PropTypes.shape(Object)),
  onClose:       PropTypes.func,
  addMallMap:    PropTypes.func.isRequired,
  updateMallMap: PropTypes.func.isRequired,
  deleteMallMap: PropTypes.func.isRequired,
};

ManageLevelsModal.defaultProps = {
  isOpen:      false,
  mallMapData: [],
  onClose:     () => {},
};

export default connect(null, { addMallMap, updateMallMap, deleteMallMap })(ManageLevelsModal);
